import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, Collapse, Divider, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatPhoneNumber } from '../../container/Common/FormatPhoneNumber';
import { DEFAULT_EMAIL_MESSAGES } from '../../utility/Constant';
import ReviewRequestAppSvg from '../../utility/Svg/ReviewRequestAppSvg';
import ReviewRequestEmailSvg from '../../utility/Svg/ReviewRequestEmailSvg';
import ReviewRequestTextSvg from '../../utility/Svg/ReviewRequestTextSvg';

// ─── Styled delivery chip ───────────────────────────────────────────────────
const StyledChip = styled(Chip)(({ $selected }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    height: 'auto',
    minHeight: '40px',

    padding: '8px 16px',

    borderRadius: '50px',
    border: $selected ? '1px solid #1976D2' : '1px solid #E0E0E0',
    background: $selected ? '#3B82F6' : '#FFF',

    flex: '1 0 0',

    fontSize: '16px',
    fontWeight: 400,
    color: $selected ? '#FFF' : '#212121',

    cursor: 'pointer',
    boxShadow: 'none',
    transition: 'all 0.15s ease',

    '&:hover': {
        background: $selected ? '#2563EB' : '#F9FAFB',
        border: $selected ? '1px solid #1D4ED8' : '1px solid #D1D5DB',
    },

    '& .MuiChip-label': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    },

    '& .MuiChip-icon': {
        marginLeft: 0,
        marginRight: 0,
    },
}));

// ─── Delivery channels config ────────────────────────────────────────────────
const CHANNELS = [
    { id: 'text', label: 'Text', Icon: ReviewRequestTextSvg, size: 20 },
    { id: 'email', label: 'Email', Icon: ReviewRequestEmailSvg, size: 20 },
    { id: 'APP', label: 'App', Icon: ReviewRequestAppSvg, size: 20 },
];

// ─── Component ───────────────────────────────────────────────────────────────
const DeliveryMethodSelector = ({
    selectedCustomer,
    setSelectedCustomer,
    deliveryMethod,
    setDeliveryMethod,
    customMessage,
    setCustomMessage,
    newCustomer,
    shopConfigData,
    lastSentMessage = '',
}) => {
    const [summaryOpen, setSummaryOpen] = useState(false);
    console.log('deliveryMethod', deliveryMethod);
    // ── Shop name from localStorage
    const shopName = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('apex-saas_details'))?.shopName || '';
        } catch {
            return '';
        }
    }, []);

    const getDynamicPlaceholder = (shopData) => {
        const customMsg = shopData?.shopThankYouEmailCustomMessage || DEFAULT_EMAIL_MESSAGES.OPENING(shopName);
        const closingMsg = shopData?.shopThankYouEmailClosingMessage || DEFAULT_EMAIL_MESSAGES.CLOSING;
        return `${customMsg}${customMsg && closingMsg ? '\n\n' : ''}${closingMsg}`.trim();
    };

    // ── Normalise selectedCustomer  always an array
    // const selectedArray = Array.isArray(selectedCustomer) ? selectedCustomer : selectedCustomer ? [selectedCustomer] : [];

    const selectedArray = useMemo(() => {
        const existing = Array.isArray(selectedCustomer) ? selectedCustomer : selectedCustomer ? [selectedCustomer] : [];
        console.log('existing', existing);
        if (existing.length === 0 && newCustomer?.firstName?.trim()) {
            return [
                {
                    id: 'new',
                    name: newCustomer.firstName,
                    phone: newCustomer.phoneNumber, // ← must be 'phone' not 'phoneNumber'
                    email: newCustomer.email,
                    isNew: true,
                },
            ];
        }

        return existing.map((c) => ({
            ...c,
            phone: c.phone || c.phoneNumber, // ← normalize existing customers too
        }));
    }, [selectedCustomer, newCustomer]);

    // const isWalkIn = selectedArray.length === 0;
    const existingCustomers = Array.isArray(selectedCustomer) ? selectedCustomer : selectedCustomer ? [selectedCustomer] : [];

    const isWalkIn = existingCustomers.length === 0;

    const isExactlyOneExisting = existingCustomers.length === 1 && !!existingCustomers[0]?.id;

    const count = selectedArray.length;
    const isSingle = count === 1 || (count == 0 && newCustomer?.firstName);
    const isMultiple = count > 1;

    const filteredChannels = isWalkIn ? CHANNELS.filter((c) => c.id !== 'APP') : CHANNELS;

    // ── Set defaults from shopConfig for EXISTING customers ──
    useEffect(() => {
        if (isWalkIn) return; // walk-in handled separately
        if (!shopConfigData) return; // wait for data to load

        const methods = [];
        if (shopConfigData?.config?.shouldSendReviewSMSToCustomer) methods.push('text');
        if (shopConfigData?.config?.shouldSendReviewEmailToCustomer) methods.push('email');
        if (shopConfigData?.config?.shouldSendReviewNotificationToCustomer) methods.push('APP');

        setDeliveryMethod(methods);
    }, [isWalkIn, shopConfigData]); // re-runs if customer type switches

    // ── Toggle a delivery method on/off
    const toggleMethod = (id) => {
        if (isWalkIn && id === 'APP') return;

        setDeliveryMethod((prev) => {
            return prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id];
        });
    };
    // ── Remove a single customer from the selection
    const handleRemoveCustomer = (id) => {
        setSelectedCustomer(selectedArray.filter((c) => c.id !== id));
    };

    // ── Icon colour
    const chipIconColor = (selected) => (selected ? '#FFF' : '#000');

    const initializedRef = useRef(false);

    useEffect(() => {
        if (!isWalkIn) return;

        const hasPhone = !!newCustomer?.phoneNumber?.trim();
        const hasEmail = !!newCustomer?.email?.trim();

        setDeliveryMethod((prev) => {
            if (!initializedRef.current) {
                initializedRef.current = true;

                let next = [];
                if (hasPhone) next.push('text');
                if (hasEmail) next.push('email');
                return next;
            }

            let next = [...prev];
            if (!hasPhone) next = next.filter((m) => m !== 'text');
            if (!hasEmail) next = next.filter((m) => m !== 'email');

            return next;
        });
    }, [newCustomer?.phoneNumber, newCustomer?.email, isWalkIn]);

    return (
        <Box>
            {isSingle ? (
                <Box
                    sx={{
                        p: '16px',
                        borderRadius: '8px',

                        background: '#FAFBFF',
                        color: '#006CDD',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        mb: 3,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <g clip-path="url(#clip0_24486_52688)">
                                <path
                                    d="M9 9C10.6575 9 12 7.6575 12 6C12 4.3425 10.6575 3 9 3C7.3425 3 6 4.3425 6 6C6 7.6575 7.3425 9 9 9ZM9 10.5C6.9975 10.5 3 11.505 3 13.5V14.25C3 14.6625 3.3375 15 3.75 15H14.25C14.6625 15 15 14.6625 15 14.25V13.5C15 11.505 11.0025 10.5 9 10.5Z"
                                    fill="#006CDD"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_24486_52688">
                                    <rect width="18" height="18" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#000' }}>{selectedArray[0].name}</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        {(selectedArray[0].phone || selectedArray[0].phoneNumber) && (
                            <Box display="flex" alignItems="center" gap={'4px'}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M11.2032 0H4.79675C4.37325 0 3.9671 0.168234 3.66764 0.467692C3.36818 0.76715 3.19995 1.1733 3.19995 1.5968V14.4024C3.19995 15.2848 3.91515 16 4.79675 16H11.2024C11.6259 16 12.0322 15.8318 12.3317 15.5324C12.6313 15.2329 12.7997 14.8268 12.8 14.4032V1.5968C12.8 1.1733 12.6317 0.76715 12.3323 0.467692C12.0328 0.168234 11.6266 0 11.2032 0ZM7.99995 15.2C7.44795 15.2 6.99995 14.8424 6.99995 14.4C6.99995 13.9576 7.44795 13.6 7.99995 13.6C8.55195 13.6 8.99995 13.9576 8.99995 14.4C8.99995 14.8424 8.55195 15.2 7.99995 15.2ZM11.2 12.8H4.79995V1.6H11.2V12.8Z"
                                        fill="#006CDD"
                                    />
                                </svg>

                                <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'rgba(0, 0, 0, 0.80)', lineHeight: '133.4%' }}>
                                    {formatPhoneNumber(selectedArray[0].phone || selectedArray[0].phoneNumber)}
                                </Typography>
                            </Box>
                        )}

                        {selectedArray[0].email && (
                            <Box display="flex" alignItems="center" gap={'5px'}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <g clip-path="url(#clip0_24486_52697)">
                                        <path
                                            d="M14.6666 3.99999C14.6666 3.26666 14.0666 2.66666 13.3333 2.66666H2.66665C1.93331 2.66666 1.33331 3.26666 1.33331 3.99999V12C1.33331 12.7333 1.93331 13.3333 2.66665 13.3333H13.3333C14.0666 13.3333 14.6666 12.7333 14.6666 12V3.99999ZM13.3333 3.99999L7.99998 7.33332L2.66665 3.99999H13.3333ZM13.3333 12H2.66665V5.33332L7.99998 8.66666L13.3333 5.33332V12Z"
                                            fill="#006CDD"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_24486_52697">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'rgba(0, 0, 0, 0.80)' }}>{selectedArray[0].email}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : isMultiple ? (
                <>
                    {/* ── Selected Customers Summary card ─────────────────────────── */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: summaryOpen ? '16px' : 0,

                            mb: 3,
                            transition: 'gap 0.2s ease',
                        }}
                    >
                        {/* Header row – click anywhere to collapse */}
                        <Box
                            onClick={() => setSummaryOpen((prev) => !prev)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <GroupIcon sx={{ color: '#006CDD', width: 24, height: 24 }} />

                            <Typography
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 500,
                                    color: '#111827',
                                    flexGrow: 1,
                                }}
                            >
                                {count} customer{count !== 1 ? 's' : ''} selected
                            </Typography>

                            <IconButton size="small" disableRipple sx={{ p: 0 }}>
                                {summaryOpen ? (
                                    <KeyboardArrowUpIcon
                                        sx={{
                                            borderRadius: '50%',
                                            background: '#334FA9',
                                            color: '#FFF',
                                            width: 30,
                                            height: 30,
                                        }}
                                    />
                                ) : (
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            borderRadius: '50%',
                                            background: '#334FA9',
                                            color: '#FFF',
                                            width: 30,
                                            height: 30,
                                        }}
                                    />
                                )}
                            </IconButton>
                        </Box>

                        {/* Collapsible customer cards grid */}
                        <Collapse in={summaryOpen}>
                            <Box sx={{ pt: 1 }}>
                                {count === 0 ? (
                                    <Typography sx={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>No customers selected</Typography>
                                ) : (
                                    <Grid container spacing={2} justifyContent="center">
                                        {selectedArray.map((cust, i) => {
                                            console.log('🚀 ~ DeliveryMethodSelector ~ cust:', cust);
                                            return (
                                                <Grid item xs={12} sm={6} md={4} key={cust.id || i}>
                                                    <div
                                                        style={{
                                                            position: 'relative',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            height: '100%',
                                                            // alignItems: 'flex-start',
                                                            // gap: '0',
                                                            padding: '16px',
                                                            borderRadius: '8px',
                                                            background: '#FAFBFF',
                                                            flex: '1 0 0',
                                                        }}
                                                    >
                                                        {/* Remove button */}
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveCustomer(cust.id);
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '12px',
                                                                right: '14px',
                                                                width: 26,
                                                                height: 26,
                                                                p: 0,
                                                                '&:hover': { background: 'rgba(0,0,0,0.06)' },
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ width: 18, height: 18, color: '#000' }} />
                                                        </IconButton>

                                                        {/* Customer name */}
                                                        <Box display="flex" alignItems="center" gap={0.5} width={'90%'} mb={'12px'}>
                                                            <div style={{ width: '18px', height: '18px' }}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                                    <g clip-path="url(#clip0_24497_51447)">
                                                                        <path
                                                                            d="M9 9C10.6575 9 12 7.6575 12 6C12 4.3425 10.6575 3 9 3C7.3425 3 6 4.3425 6 6C6 7.6575 7.3425 9 9 9ZM9 10.5C6.9975 10.5 3 11.505 3 13.5V14.25C3 14.6625 3.3375 15 3.75 15H14.25C14.6625 15 15 14.6625 15 14.25V13.5C15 11.505 11.0025 10.5 9 10.5Z"
                                                                            fill="#006CDD"
                                                                        />
                                                                    </g>
                                                                    <defs>
                                                                        <clipPath id="clip0_24497_51447">
                                                                            <rect width="18" height="18" fill="white" />
                                                                        </clipPath>
                                                                    </defs>
                                                                </svg>
                                                            </div>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    color: '#000',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    lineHeight: '133.4%',
                                                                }}
                                                            >
                                                                {cust.name}
                                                            </Typography>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '4px',
                                                                width: '100%',
                                                                mb: '4px',
                                                            }}
                                                        >
                                                            {/* Phone */}
                                                            {(cust.phone || cust.phoneNumber) && (
                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                    >
                                                                        <g clip-path="url(#clip0_24497_51484)">
                                                                            <path
                                                                                d="M11.2034 0H4.797C4.3735 0 3.96735 0.168234 3.66789 0.467692C3.36843 0.76715 3.2002 1.1733 3.2002 1.5968V14.4024C3.2002 15.2848 3.9154 16 4.797 16H11.2026C11.6262 16 12.0324 15.8318 12.332 15.5324C12.6316 15.2329 12.8 14.8268 12.8002 14.4032V1.5968C12.8002 1.1733 12.632 0.76715 12.3325 0.467692C12.033 0.168234 11.6269 0 11.2034 0ZM8.0002 15.2C7.4482 15.2 7.0002 14.8424 7.0002 14.4C7.0002 13.9576 7.4482 13.6 8.0002 13.6C8.5522 13.6 9.0002 13.9576 9.0002 14.4C9.0002 14.8424 8.5522 15.2 8.0002 15.2ZM11.2002 12.8H4.8002V1.6H11.2002V12.8Z"
                                                                                fill="#006CDD"
                                                                            />
                                                                        </g>
                                                                        <defs>
                                                                            <clipPath id="clip0_24497_51484">
                                                                                <rect width="16" height="16" fill="white" />
                                                                            </clipPath>
                                                                        </defs>
                                                                    </svg>
                                                                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'rgba(0,0,0,0.80)' }}>
                                                                        {formatPhoneNumber(cust.phone || cust.phoneNumber)}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {/* Email */}
                                                            {cust.email && (
                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                    >
                                                                        <g clip-path="url(#clip0_24497_51487)">
                                                                            <path
                                                                                d="M14.6668 3.99984C14.6668 3.2665 14.0668 2.6665 13.3335 2.6665H2.66683C1.9335 2.6665 1.3335 3.2665 1.3335 3.99984V11.9998C1.3335 12.7332 1.9335 13.3332 2.66683 13.3332H13.3335C14.0668 13.3332 14.6668 12.7332 14.6668 11.9998V3.99984ZM13.3335 3.99984L8.00016 7.33317L2.66683 3.99984H13.3335ZM13.3335 11.9998H2.66683V5.33317L8.00016 8.6665L13.3335 5.33317V11.9998Z"
                                                                                fill="#006CDD"
                                                                            />
                                                                        </g>
                                                                        <defs>
                                                                            <clipPath id="clip0_24497_51487">
                                                                                <rect width="16" height="16" fill="white" />
                                                                            </clipPath>
                                                                        </defs>
                                                                    </svg>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: '14px',
                                                                            fontWeight: 400,
                                                                            color: 'rgba(0,0,0,0.80)',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            lineHeight: '133.4%',
                                                                        }}
                                                                    >
                                                                        {cust.email}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </div>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Box>
                        </Collapse>
                        <Divider sx={{ mt: 2 }} />
                    </Box>
                </>
            ) : null}

            {/* ── Choose Delivery Method heading ──────────────────────────── */}
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                {/* Paper-plane icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M23.9837 2.42229C24.1007 1.87765 23.5667 1.41842 23.0459 1.61717L0.452326 10.2421C0.319493 10.2928 0.205157 10.3826 0.124404 10.4996C0.0436507 10.6167 0.000275463 10.7554 1.30767e-06 10.8976C-0.000272848 11.0398 0.0425669 11.1787 0.122868 11.2961C0.203169 11.4134 0.317158 11.5036 0.449795 11.5549L6.79675 14.0062V21.727C6.79674 21.8852 6.85008 22.0388 6.94817 22.163C7.04625 22.2871 7.18334 22.3745 7.33726 22.4111C7.49118 22.4477 7.65295 22.4313 7.7964 22.3646C7.93986 22.2979 8.05661 22.1847 8.1278 22.0434L10.7528 16.8343L17.1589 21.5885C17.5485 21.8777 18.1087 21.6938 18.2504 21.229C24.2316 1.61872 23.973 2.47155 23.9837 2.42229ZM18.4065 4.89342L7.40021 12.7317L2.66457 10.9028L18.4065 4.89342ZM8.20298 13.8864L17.7967 7.0541C9.54144 15.7629 9.97259 15.3045 9.93659 15.353C9.88311 15.425 10.0296 15.1445 8.20298 18.7693V13.8864ZM17.1962 19.8651L11.5574 15.6803L21.7531 4.92436L17.1962 19.8651Z"
                        fill="#006CDD"
                    />
                </svg>

                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        letterSpacing: '0.15px',
                        lineHeight: '150%',
                        color: '#111827',
                    }}
                >
                    Choose Delivery Method
                </Typography>
            </Stack>

            {/* ── Delivery method chips (multi-select toggle) ─────────────── */}
            <Stack direction="row" spacing={2} mb={1.5} width="100%">
                {filteredChannels.map(({ id, label, Icon, size }) => {
                    // const selected = deliveryMethod.includes(id);
                    const selected = Array.isArray(deliveryMethod) && deliveryMethod.includes(id);

                    const isDisabled = isWalkIn && ((id === 'text' && !newCustomer?.phoneNumber?.trim()) || (id === 'email' && !newCustomer?.email?.trim()));
                    return (
                        <StyledChip
                            key={id}
                            // disabled={isDisabled}
                            $selected={selected}
                            onClick={() => toggleMethod(id)}
                            icon={<Icon color={chipIconColor(selected)} size={size} />}
                            label={label}
                            aria-label={`${label} delivery method`}
                            aria-pressed={selected}
                            clickable
                        />
                    );
                })}
            </Stack>

            {/* ── Last sent on ───────────────────────────────── */}
            {isExactlyOneExisting && !!lastSentMessage && (
                <Typography
                    sx={{
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 400,
                        letterSpacing: '0.15px',
                        lineHeight: '150%',
                        color: 'rgba(0, 0, 0, 0.60)',
                        mb: 1,
                    }}
                >
                    {lastSentMessage}
                </Typography>
            )}

            {/* ── Customize email message ──────────────────────────────────── */}
            {deliveryMethod.includes('email') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: '20px', gap: '8px' }}>
                    <Typography variant="body1" fontWeight={500} fontSize={16} sx={{ letterSpacing: '0.15px' }}>
                        Customize email message
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder={getDynamicPlaceholder(shopConfigData)}
                        sx={{
                            '& .MuiInputBase-input::placeholder': {
                                fontWeight: 400,
                                lineHeight: '130%',
                                letterSpacing: '0.15px',
                                color: '#9E9E9E',
                                opacity: 1,
                            },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                padding: '12px!important',
                                // paddingBottom: customEmailMessage ? '12px!important' : '0!important',
                                boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
                                '& fieldset': {
                                    border: '1px solid rgba(0, 0, 0, 0.10)',
                                },
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default DeliveryMethodSelector;
